import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import Modal from "@/components/Modal";
import { useForm } from "react-hook-form";
import FormDatePicker from "@/components/FormDatePicker";
import { updateProject } from "@/api/projectsApi";
import { ProjectRecord } from "@/types";
import SVGEditNote from "@/assets/icons/SVGEditNote";
import { StyledIconButton } from "@/components/styled";
import { getErrorMessage } from "@/utils/getErrorMessage";
import dayjs from "dayjs";

type Props = {
  projectId: ProjectRecord["id"];
  currentDate: string | null;
  onSuccess?: () => void;
};

type FormData = {
  estimated_installation_date: string | null;
};

const EditEstimatedInstallationDateModal = ({
  projectId,
  currentDate,
  onSuccess,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    reset,
  } = useForm<FormData>({
    defaultValues: {
      estimated_installation_date: currentDate || null,
    },
  });

  const handleOpen = () => {
    reset({
      estimated_installation_date: currentDate || null,
    });
    setOpen(true);
    setError(null);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setOpen(false);
      setError(null);
      reset();
    }
  };

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      const formData = new FormData();
      if (data.estimated_installation_date) {
        formData.append(
          "estimated_installation_date",
          dayjs(data.estimated_installation_date).toISOString()
        );
      }

      await updateProject(projectId, formData);

      setOpen(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: unknown) {
      setError(
        getErrorMessage(err, "Failed to update estimated installation date. Please try again.")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <StyledIconButton onClick={handleOpen}>
        <SVGEditNote />
      </StyledIconButton>

      <Modal
        open={open}
        onClose={handleClose}
        title="Edit Estimated Installation Date"
        boxSx={{
          maxWidth: 500,
          width: "100%",
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ mt: 3, mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 2, color: "textLightGray3" }}>
              Update the estimated installation date for this project.
            </Typography>

            <FormDatePicker
              name="estimated_installation_date"
              control={control}
              label="Estimated Installation Date"
            />

            {error && (
              <Typography
                variant="body2"
                sx={{ color: "error.main", mt: 2, fontSize: "14px" }}
              >
                {error}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "flex-end",
              mt: 3,
            }}
          >
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={isSubmitting}
              sx={{
                borderColor: "#D0D5DD",
                color: "#344054",
                "&:hover": {
                  borderColor: "#D0D5DD",
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              sx={{
                backgroundColor: "#0076CE",
                color: "#fff",
                "&:hover": {
                  backgroundColor: "#0056a3",
                },
                "&:disabled": {
                  backgroundColor: "#B0B0B0",
                  color: "#fff",
                },
              }}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
          </Box>
        </form>
      </Modal>
    </>
  );
};

export default EditEstimatedInstallationDateModal;
